import { useEffect } from 'react';
import { getUserShape } from '@atlassian/jira-issue-field-assignee/src/common/utils.tsx';
import { useFieldValue } from '@atlassian/jira-issue-field-base/src/services/field-value-service/index.tsx';
import { AssigneeKey } from '@atlassian/jira-providers-issue/src/model/issue-system-fields.tsx';
import { useDiffCheck } from '../../../../common/hooks/use-diff-check';
import type { Card } from '../../../../model/types';
import { useBacklogActionCreator } from '../../../../state';
import { cardAssigneeSet } from '../../../../state/actions/assignee';

export type Props = {
	card: Card;
};

const FieldSyncAssignee = ({ card: { key, id, assignee: assigneeFromBacklog } }: Props) => {
	const {
		id: assigneeIdFromBacklog,
		displayName: assigneeNameFromBacklog,
		avatarUrl: assigneeAvatarUrlFromBacklog,
	} = assigneeFromBacklog || {};

	const [assigneeFromExtractedFields, { setFieldValue: setAssigneeInExtractedFields }] =
		useFieldValue({
			issueKey: key,
			fieldKey: AssigneeKey,
		});

	const { accountId: assigneeIdFromExtractedFields, displayName: assigneeNameFromExtractedFields } =
		assigneeFromExtractedFields || {};

	const assigneeAvatarUrlFromExtractedFields = assigneeFromExtractedFields?.avatarUrls?.['48x48'];

	const assigneeFromBacklogChanged = useDiffCheck([assigneeIdFromBacklog]);
	const assigneeFromExtractedFieldsChanged = useDiffCheck([assigneeIdFromExtractedFields]);
	const setAssigneeInBacklog = useBacklogActionCreator(cardAssigneeSet);

	const storesNeedToSync = assigneeIdFromBacklog !== assigneeIdFromExtractedFields;

	useEffect(() => {
		const assigneeFromBacklogIsDefined = assigneeFromBacklog !== undefined;

		// ASSIGNEE SYNC: BACKLOG -> EXTRACTED FIELDS
		// initial value, realtime...
		if (assigneeFromBacklogIsDefined && assigneeFromBacklogChanged && storesNeedToSync) {
			setAssigneeInExtractedFields(
				key,
				AssigneeKey,
				// if assignee id is '', null or undefined => set assignee to null
				assigneeIdFromBacklog
					? getUserShape(
							assigneeIdFromBacklog,
							// @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
							assigneeNameFromBacklog,
							assigneeAvatarUrlFromBacklog,
						)
					: null,
			);
		}
	}, [
		key,
		assigneeFromBacklog,
		assigneeIdFromBacklog,
		assigneeNameFromBacklog,
		assigneeAvatarUrlFromBacklog,
		assigneeFromBacklogChanged,
		storesNeedToSync,
		setAssigneeInExtractedFields,
	]);

	useEffect(() => {
		const assigneeFromExtractedFieldsIsDefined = assigneeFromExtractedFields !== undefined;

		// ASSIGNEE SYNC: EXTRACTED FIELDS -> BACKLOG REDUX
		// issue view updates, inline assignee edit...
		if (
			// Ensure no sync will be called by Backlog as Backlog is the driver
			!assigneeFromBacklogChanged &&
			assigneeFromExtractedFieldsIsDefined &&
			assigneeFromExtractedFieldsChanged &&
			storesNeedToSync
		) {
			setAssigneeInBacklog(
				id,
				// if assignee id is '', null or undefined => set assignee to null
				assigneeIdFromExtractedFields
					? {
							id: assigneeIdFromExtractedFields,
							displayName: assigneeNameFromExtractedFields,
							avatarUrl: assigneeAvatarUrlFromExtractedFields,
						}
					: null,
			);
		}
	}, [
		id,
		assigneeFromBacklogChanged,
		assigneeFromExtractedFields,
		assigneeIdFromExtractedFields,
		assigneeNameFromExtractedFields,
		assigneeAvatarUrlFromExtractedFields,
		assigneeFromExtractedFieldsChanged,
		storesNeedToSync,
		setAssigneeInBacklog,
	]);

	return null;
};

export default FieldSyncAssignee;
